<template>
  <div class="evaluate-applications">
    <h3 class="mb-4" style="color: #8b69a9">Projektbewerbungen auswerten</h3>

    <b-alert :show="alert.show" :variant="alert.variant" dismissible @dismissed="alert.show = false">
      {{ alert.message }}
    </b-alert>

    <div v-if="isLoading" class="text-center my-5">
      <b-spinner label="Lade Daten..." variant="primary"></b-spinner>
    </div>

    <div v-else class="table-responsive bg-white rounded shadow-sm p-3">
      <table class="table table-striped table-hover align-middle">
        <thead>
          <tr>
            <th>Student/in</th>
            <th>Prio 1</th>
            <th>Prio 2</th>
            <th>Prio 3</th>
            <th>Wunschpartner</th>
            <th>Kommentare</th>
            <th>Finale Zuteilung</th>
            <th>Aktion</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in tableData" :key="row.id">
            <td>
              <strong>{{ row.studentName }}</strong>
            </td>
            <td>{{ row.prio1 }}</td>
            <td>{{ row.prio2 }}</td>
            <td>{{ row.prio3 }}</td>
            <td>{{ row.partnerName }}</td>
            <td>
              <span :title="row.comments" class="text-truncate d-inline-block" style="max-width: 150px; cursor: help">
                {{ row.comments }}
              </span>
            </td>
            <td>
              <b-form-select v-model="row.selectedProjectToAssign" :options="projectOptions" size="sm">
                <template #first>
                  <b-form-select-option :value="null" disabled>-- Bitte wählen --</b-form-select-option>
                </template>
              </b-form-select>
            </td>
            <td>
              <b-button
                size="sm"
                variant="success"
                @click="assignStudent(row)"
                :disabled="!row.selectedProjectToAssign || isSaving === row.id"
              >
                <b-spinner small v-if="isSaving === row.id"></b-spinner>
                <span v-else>Speichern</span>
              </b-button>
            </td>
          </tr>
          <tr v-if="tableData.length === 0">
            <td colspan="8" class="text-center text-muted py-4">Keine Bewerbungen gefunden.</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import axios from '../../../../axios'

export default {
  name: 'EvaluateProjectApplications',
  data() {
    return {
      isLoading: true,
      isSaving: null,
      tableData: [],
      projectOptions: [],
      alert: {
        show: false,
        message: '',
        variant: 'success'
      }
    }
  },
  async created() {
    await this.loadData()
  },
  methods: {
    async loadData() {
      this.isLoading = true
      try {
        // Lade alle relevanten Ressourcen parallel für maximale Performance
        const [appsRes, projectsRes, usersRes, attrRes] = await Promise.all([
          axios.get('project-applications/'),
          axios.get('projects/'),
          axios.get('users/'),
          axios.get('user-project-attributions/')
        ])

        const applications = appsRes.data
        const projects = projectsRes.data
        const users = usersRes.data
        const attributions = attrRes.data

        // Optionen für das Zuteilungs-Dropdown
        this.projectOptions = projects.map((p) => ({ value: p.id, text: p.name }))

        // Datenstrukturen für die Tabelle mappen
        this.tableData = applications.map((app) => {
          const student = users.find((u) => u.id === app.student)
          const p1 = projects.find((p) => p.id === app.wish_one)
          const p2 = projects.find((p) => p.id === app.wish_two)
          const p3 = projects.find((p) => p.id === app.wish_three)
          const partner = users.find((u) => u.id === app.preferred_partner)

          // Prüfen, ob der Student bereits einem Projekt zugewiesen wurde
          const attribution = attributions.find((a) => a.user === app.student || (a.user && a.user.id === app.student))

          const assignedProjectId = attribution
            ? typeof attribution.project === 'object'
              ? attribution.project.id
              : attribution.project
            : null

          return {
            id: app.id,
            studentId: app.student,
            studentName: student ? `${student.first_name} ${student.last_name}` : 'Unbekannt',
            prio1: p1 ? p1.name : '-',
            prio2: p2 ? p2.name : '-',
            prio3: p3 ? p3.name : '-',
            partnerName: partner ? `${partner.first_name} ${partner.last_name}` : '-',
            comments: app.comments || '-',
            attributionId: attribution ? attribution.id : null,
            selectedProjectToAssign: assignedProjectId
          }
        })
      } catch (error) {
        this.showAlert('Fehler beim Laden der Auswertungsdaten.', 'danger')
        // eslint-disable-next-line no-console
        console.error(error)
      } finally {
        this.isLoading = false
      }
    },

    async assignStudent(row) {
      this.isSaving = row.id
      try {
        const payload = {
          user: row.studentId,
          project: row.selectedProjectToAssign
        }

        // Entscheide ob wir updaten (PATCH) oder neu anlegen (POST)
        if (row.attributionId) {
          await axios.patch(`user-project-attributions/${row.attributionId}/`, payload)
        } else {
          const res = await axios.post('user-project-attributions/', payload)
          row.attributionId = res.data.id // Speichere ID für spätere Änderungen
        }

        this.showAlert(`Student ${row.studentName} wurde erfolgreich zugewiesen!`, 'success')
      } catch (error) {
        this.showAlert('Fehler bei der Zuweisung.', 'danger')
        // eslint-disable-next-line no-console
        console.error(error)
      } finally {
        this.isSaving = null
      }
    },

    showAlert(message, variant = 'success') {
      this.alert.message = message
      this.alert.variant = variant
      this.alert.show = true
      // Automatisches Ausblenden der Meldung
      setTimeout(() => {
        this.alert.show = false
      }, 4000)
    }
  }
}
</script>

<style scoped>
.evaluate-applications {
  width: 100%;
}
</style>
